import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  GET_SCREENER_DATA,
  ScreenerDimension,
  ScreenerSortCol,
} from '@revelio/data-access';

import type { CompanyRicsIds } from './useCompanyRics';

export interface Competitor {
  id: number;
  shortName: string;
  longName: string;
  linkedInUrl: string | null;
  hqLocation: string | null;
}

export interface IndustryFallbackFilters {
  rics: CompanyRicsIds;
  /** Country geo ID for HQ filtering */
  countryId?: number | null;
  /** MSA geo ID for HQ filtering */
  msaId?: number | null;
}

interface UseCompetitorsResult {
  competitors: Competitor[];
  isLoading: boolean;
  error: string | null;
  /** True when using RICS industry fallback instead of direct competitors */
  isIndustryFallback: boolean;
}

/**
 * Build a human-readable HQ location from screener company_info fields.
 * Prefers metro area + country, falls back to state + country or just country.
 */
const buildHqLocation = (
  info:
    | {
        hq_metro_area?: string | null;
        hq_state?: string | null;
        hq_country?: string | null;
      }
    | null
    | undefined
): string | null => {
  if (!info) return null;
  const parts: string[] = [];
  if (info.hq_metro_area) parts.push(info.hq_metro_area);
  else if (info.hq_state) parts.push(info.hq_state);
  if (info.hq_country) parts.push(info.hq_country);
  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Parse screener entities into Competitor objects.
 */
const parseEntities = (
  entities:
    | ReadonlyArray<{
        entity_id: number;
        short_name?: string | null;
        long_name?: string | null;
        entity_info?: {
          company_info?: {
            linkedin_url?: string | null;
            hq_metro_area?: string | null;
            hq_state?: string | null;
            hq_country?: string | null;
          } | null;
        } | null;
      } | null>
    | null
    | undefined,
  excludeRcid?: number | null
): Competitor[] => {
  if (!entities) return [];
  return entities
    .filter(
      (e): e is NonNullable<typeof e> =>
        e !== null &&
        e !== undefined &&
        e.entity_id !== null &&
        e.entity_id !== undefined &&
        e.entity_id !== excludeRcid
    )
    .map((e) => ({
      id: e.entity_id,
      shortName: e.short_name ?? '',
      longName: e.long_name ?? '',
      linkedInUrl: e.entity_info?.company_info?.linkedin_url ?? null,
      hqLocation: buildHqLocation(e.entity_info?.company_info),
    }));
};

/**
 * Fetches competitors using the Screener API with the `competitors_of` filter.
 * If that returns no results and RICS industry + geo data are provided, falls
 * back to querying companies in the same RICS industry and geographic area.
 */
export const useCompetitors = (
  companyRcid: number | null,
  maxCompetitors = 15,
  fallbackFilters?: IndustryFallbackFilters | null
): UseCompetitorsResult => {
  // Minimum headcount to filter out tiny/shell companies
  const MIN_HEADCOUNT = 10;

  // 1. Primary: try competitors_of with quality filters
  const [
    { data: primaryData, fetching: primaryFetching, error: primaryError },
  ] = useQuery({
    query: GET_SCREENER_DATA,
    variables: {
      filters: {
        dim: ScreenerDimension.Company,
        primary_selector: {},
        segments: {
          headcount: [{ start_val: MIN_HEADCOUNT }],
        },
        entity_info_selectors: {
          company_info_selectors: {
            competitors_of: companyRcid ? [companyRcid] : [],
          },
        },
        sorting: {
          sort_by: ScreenerSortCol.SortBySalary,
          ascending: false,
          num_col: 0,
        },
        pagination: {
          offset: 0,
          limit: maxCompetitors,
        },
      },
    },
    pause: !companyRcid,
  });

  const primaryCompetitors = useMemo(
    () => parseEntities(primaryData?.screener?.entities),
    [primaryData]
  );

  // Build RICS + geo fallback filter
  const fallbackVariables = useMemo(() => {
    const rics = fallbackFilters?.rics;
    if (!rics) return null;

    // Most granular RICS level available
    const ricsSelector: Record<string, number[]> = {};
    if (rics.ricsK400) {
      ricsSelector['rics_k400'] = [rics.ricsK400];
    } else if (rics.ricsK50) {
      ricsSelector['rics_k50'] = [rics.ricsK50];
    } else if (rics.ricsK10) {
      ricsSelector['rics_k10'] = [rics.ricsK10];
    } else {
      return null;
    }

    // Add HQ geo filtering — try MSA first, fall back to country
    const hqFilters: Record<string, number[]> = {};
    if (fallbackFilters.msaId) {
      hqFilters['hq_msa'] = [fallbackFilters.msaId];
    } else if (fallbackFilters.countryId) {
      hqFilters['hq_country'] = [fallbackFilters.countryId];
    }

    return {
      filters: {
        dim: ScreenerDimension.Company,
        primary_selector: ricsSelector,
        segments: {
          headcount: [{ start_val: MIN_HEADCOUNT }],
        },
        entity_info_selectors: {
          company_info_selectors:
            Object.keys(hqFilters).length > 0 ? hqFilters : undefined,
        },
        sorting: {
          sort_by: ScreenerSortCol.SortBySalary,
          ascending: false,
          num_col: 0,
        },
        pagination: {
          offset: 0,
          limit: maxCompetitors,
        },
      },
    };
  }, [fallbackFilters, maxCompetitors]);

  // 2. Fallback: search by RICS industry + geo when competitors_of yields nothing
  const needsFallback =
    !primaryFetching &&
    !primaryError &&
    primaryCompetitors.length === 0 &&
    fallbackVariables !== null;

  const pausedVariables = {
    filters: {
      dim: ScreenerDimension.Company,
      primary_selector: {},
      segments: {},
      entity_info_selectors: {},
      pagination: { offset: 0, limit: 1 },
    },
  };

  const [
    { data: fallbackData, fetching: fallbackFetching, error: fallbackError },
  ] = useQuery({
    query: GET_SCREENER_DATA,
    variables: fallbackVariables ?? pausedVariables,
    pause: !needsFallback,
  });

  const fallbackCompetitors = useMemo(
    () => parseEntities(fallbackData?.screener?.entities, companyRcid),
    [fallbackData, companyRcid]
  );

  const isIndustryFallback = needsFallback && fallbackCompetitors.length > 0;
  const competitors =
    primaryCompetitors.length > 0 ? primaryCompetitors : fallbackCompetitors;
  const isLoading = primaryFetching || (needsFallback && fallbackFetching);

  let error: string | null = null;
  if (primaryError) {
    error = 'Failed to load competitors';
  } else if (needsFallback && fallbackError) {
    error = 'Failed to load industry peers';
  }

  return {
    competitors,
    isLoading,
    error,
    isIndustryFallback,
  };
};
